import React from 'react';
import { AlertCircle, RotateCw, WifiOff, QrCode } from 'lucide-react';

/**
 * QrInvalidView - 読み取ったQRが使えなかったときに作業者へ理由を伝える画面
 *
 * なぜ専用画面が必要か:
 * 現場に貼った紙は回収できない。管理者がQRポイントを削除・無効化した後も、
 * その掲示物はしばらく壁に残り、作業者はスキャンし続ける。
 * 以前はトーストを出して公開LPへ遷移していたため、数秒でトーストが消えると
 * 「報告しようとしたのに広告ページが出た」という状態になり、
 * 何が起きたのか作業者には分からなかった。
 *
 * 作業者は報告しようとしてくれた人なので、次にとる行動まで示す。
 */

export type QrInvalidReason = 'inactive' | 'network';

interface Props {
  reason: QrInvalidReason;
  /** 読み取ったトークン。担当者へ伝える手がかりとして見せる */
  token?: string;
  /** 通信エラー時の再試行 */
  onRetry: () => void;
  onGoHome: () => void;
}

export const QrInvalidView: React.FC<Props> = ({ reason, token, onRetry, onGoHome }) => {
  const isNetwork = reason === 'network';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <main className="flex-1 max-w-md mx-auto w-full px-5 py-10">
        <div className="bg-white border border-slate-200 rounded-2xl p-7 text-center shadow-sm">
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
              isNetwork ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-600'
            }`}
          >
            {isNetwork ? <WifiOff size={26} /> : <AlertCircle size={26} />}
          </div>

          {isNetwork ? (
            <>
              <h1 className="text-lg font-bold text-industrial-900">
                通信できませんでした
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                QRコードは正しく読み取れています。
                <br />
                電波の良い場所で、もう一度お試しください。
              </p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold text-industrial-900">
                このQRコードは、今は使えません
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                掲示が古いか、報告先が変更された可能性があります。
                <br />
                <strong className="text-industrial-900">
                  お手数ですが、現場の安全担当者にこの掲示のことをお伝えください。
                </strong>
              </p>
              <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500">
                報告しようとしていただき、ありがとうございます。
                <br />
                急を要する内容の場合は、口頭で担当者へ直接お伝えください。
              </p>
            </>
          )}

          <div className="mt-6 space-y-2">
            {isNetwork && (
              <button
                onClick={onRetry}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-safety-600 px-4 py-3 text-sm font-bold text-white hover:bg-safety-700"
              >
                <RotateCw size={16} /> もう一度試す
              </button>
            )}
            <button
              onClick={onGoHome}
              className={`w-full rounded-xl px-4 py-3 text-sm font-bold ${
                isNetwork
                  ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  : 'bg-safety-600 text-white hover:bg-safety-700'
              }`}
            >
              安全ポスト＋について見る
            </button>
          </div>
        </div>

        {token && (
          <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
            <QrCode size={12} />
            読み取ったコード: <span className="font-mono">{token}</span>
          </p>
        )}
      </main>
    </div>
  );
};
